import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { settingsSelectors, State, UserType } from '@wam/shared';
import {
  bufferTime,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  Subject,
  takeUntil,
} from 'rxjs';
import { HelpWidgetService } from './help-widget.service';
import { HelpdocsService } from './helpdocs.service';
import { NavigationEnd, Router } from '@angular/router';
import { Tabs } from '../tabs.enum';
import { TabDisplayAggregatorService } from '../tab-display-aggregator.service';
import { take } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

declare global {
  interface Window {
    hdlh?: {
      brand: string;
      primary_color: string;
      suggestions: string[];
      onShow: () => void;
      onHide: () => void;
    };
  }
}

declare let Lighthouse: {
  navigate: (type, id?) => void;
  hide: () => void;
};

declare function amazon_connect(...args: unknown[]): void;

@Component({
  selector: 'help-widget',
  templateUrl: './help-widget.component.html',
  styleUrls: ['help-widget.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AsyncPipe, TranslatePipe],
})
export class HelpWidgetComponent implements OnInit, OnDestroy {
  private store = inject<Store<State>>(Store);
  private router = inject(Router);
  private cd = inject(ChangeDetectorRef);
  private helpdocsService = inject(HelpdocsService);
  private helpWidgetService = inject(HelpWidgetService);
  private tabDisplayAggregator = inject(TabDisplayAggregatorService);

  readonly tabTitles = input<Map<Tabs, Observable<string>>>();
  extendSupport = false;
  showWidgetAnimation = false;

  showsHelp$ = combineLatest([
    this.store.pipe(select(settingsSelectors.getUserType)),
    this.router.events.pipe(bufferTime(500)),
  ]).pipe(
    map(([type]) => ![UserType.ADMIN].includes(type) && !window.location.href.includes('eula')),
    distinctUntilChanged(),
  );

  helpCategories$ = this.tabDisplayAggregator.getDisplayedTabs().pipe(
    map((displayed) =>
      [...this.tabTitles()]
        .filter(([key]) => displayed.includes(Tabs[key]))
        .map(([key, value]) => ({
          title: value,
          tags: this.helpdocsService.getTagsForTab(key),
        })),
    ),
  );

  private destroyed$ = new Subject<void>();

  ngOnInit(): void {
    this.displayHelpWidget();
    this.hideWidgetAnimation();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  closeHelpCategoriesMenu(): void {
    this.extendSupport = false;
    this.cd.detectChanges();
    this.extendSupport = true;
    this.cd.detectChanges();
  }

  updateSuggestions(tags: string[]): void {
    window.hdlh.suggestions = tags.map((tag) => `tag:${tag}`);
    if (this.extendSupport) {
      Lighthouse.navigate('suggestions');
    }
  }

  openChat(): void {
    document.getElementById('amazon-connect-open-widget-button')?.click();
    Lighthouse.hide();
  }

  closeChat(): void {
    document.getElementById('amazon-connect-close-widget-button')?.click();
  }

  private displayHelpWidget(): void {
    this.showsHelp$.pipe(takeUntil(this.destroyed$)).subscribe((showHelp) => {
      if (showHelp) {
        this.initializeChat();

        this.helpWidgetService.show();
        window.hdlh.brand = '';
        window.hdlh.primary_color = '#fbfcfe';

        this.keepUpdatingSuggestions();

        window.hdlh.onShow = () => {
          this.extendSupport = true;
          this.showWidgetAnimation = false;
          this.closeChat();
          this.cd.detectChanges();
          Lighthouse.navigate('suggestions');
        };

        window.hdlh.onHide = () => {
          this.extendSupport = false;
          this.cd.detectChanges();
        };
      } else {
        this.helpWidgetService.hide();
      }
    });
  }

  private initializeChat(): void {
    this.store
      .pipe(select(settingsSelectors.getAmazonConnectSettings), take(1))
      .subscribe(({ id, snippetId }) => {
        (function (w, d, x, id) {
          const s: HTMLScriptElement = d.createElement('script');
          s.src =
            'https://waterford-connect.my.connect.aws/connectwidget/static/amazon-connect-chat-interface-client.js';
          s.async = true;
          s.id = id;
          d.getElementsByTagName('head')[0].appendChild(s);
          w[x] =
            w[x] ||
            function () {
              (w[x].ac = w[x].ac || []).push(arguments);
            };
        })(window, document, 'amazon_connect', id);
        amazon_connect('styles', {
          iconType: 'CHAT',
          openChat: { color: '#ffffff', backgroundColor: '#123456' },
          closeChat: { color: '#ffffff', backgroundColor: '#123456' },
        });
        amazon_connect('snippetId', snippetId);
        amazon_connect('supportedMessagingContentTypes', [
          'text/plain',
          'text/markdown',
          'application/vnd.amazonaws.connect.message.interactive',
          'application/vnd.amazonaws.connect.message.interactive.response',
        ]);
      });
  }

  private keepUpdatingSuggestions(): void {
    this.helpdocsService.currentTags$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((tags) => this.updateSuggestions(tags));
  }

  private hideWidgetAnimation(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        if (this.router.url === '/') {
          this.showWidgetAnimation = this.helpWidgetService.shouldShowAnimation();
          if (this.showWidgetAnimation) {
            this.helpWidgetService.storeAnimationProperty();
          }
        }
      });
  }
}
